import React from 'react';
import { FormattedMessage } from 'react-intl';
import Saving from 'components/common/Saving';
import { Heading04, Heading06 } from '@uds/typography';
import Shortcode from 'components/Shortcode';
import Badge, { Kind as BadgeKind } from '@uds/badge';
import Prices from 'presentation/components/common/Price';
import { RenewalPeriod, Product, useProductPrice, selectors as prodSelectors } from '@uds/product';
import { Layout, PricingStrategy } from 'components/common/ProductPrice/constants';
import { isNilOrEmpty, isObject } from 'presentation/utils/generalUtils';
import Colorway, { Colorways } from '@uds/colorway';

import { getDisplayPeriod } from './utils';
import LegalText from './LegalText';
import getClassNames from './ProductPrice.stylesheet';

const { useShouldIncludeTax } = prodSelectors;

type SavingType = {
  type: string;
  value: number;
  currency: string;
};

const withSavingValue = ({ saving }: { saving?: SavingType }) => {
  return isObject(saving) && !isNilOrEmpty(saving.value);
};

const withBadge = ({ saving, hasDiscount, badge }: { saving?: SavingType; hasDiscount?: boolean; badge?: string }) => {
  return withSavingValue({ saving }) || hasDiscount === true || !isNilOrEmpty(badge);
};

// returns true if we are in a viewMode not in given array
//   0 = retail price with setup cost underneath (default)
//   1 = no setup cost, price incs. upfront cost
//   2 = no setup cost, just show retail
const excludeViewModes = (viewModes: Array<number>, pricingStrategy: PricingStrategy) => {
  const viewMode = pricingStrategy.viewMode;
  const isViewModeIncluded = (viewModes ?? []).includes(viewMode);
  return !isViewModeIncluded;
};

const shouldShowSetupFee = ({
  hasUpFrontCost,
  showFee,
  pricingStrategy,
}: {
  hasUpFrontCost?: boolean;
  showFee?: boolean;
  pricingStrategy: PricingStrategy;
}) => {
  return hasUpFrontCost && showFee && excludeViewModes([1, 2], pricingStrategy);
};
// eslint-disable-next-line no-underscore-dangle
export const __test__ = {
  shouldShowSetupFee,
};

interface OuterProps {
  colorway?: Colorways;
  showFee?: boolean;
  showLegalCaveat?: boolean;
  priceLayout?: Layout;
  viewMode?: number;
  product: Product;
  pricingStrategy: PricingStrategy;
  badge?: string;
  legalText?: string;
  saving?: SavingType;
}

const ProductPrice = ({
  colorway = Colorways.LIGHT,
  saving,
  badge,
  showLegalCaveat = true,
  showFee = true,
  legalText,
  viewMode,
  product,
  pricingStrategy,
  priceLayout,
}: OuterProps) => {
  const includeTax = useShouldIncludeTax();
  const { currency, price, upFront, discountPerc, hasUpFrontCost, hasDiscount } = useProductPrice({
    product,
    viewMode,
    displayInterval: pricingStrategy?.paymentIntervalDisplay,
    term: pricingStrategy?.pricingTier?.term,
    interval: pricingStrategy?.pricingTier?.paymentInterval,
    includeTax,
  });
  const hasSaving = withSavingValue({ saving });
  const hasBadge = withBadge({
    saving,
    hasDiscount,
    badge,
  });
  const renewalPeriod = getDisplayPeriod({ product, pricingStrategy });
  const showSetupFee = shouldShowSetupFee({
    pricingStrategy,
    hasUpFrontCost,
    showFee,
  });

  const proposition = pricingStrategy?.proposition;
  const hasProposition = !isNilOrEmpty(proposition);

  const classNames = getClassNames({
    colorway,
    priceLayout,
    hasProposition,
    hasBadge,
  });
  return (
    <Colorway colorway={colorway} className={classNames.root}>
      <div className={classNames.priceWrapper}>
        <Choose>
          <When condition={hasSaving}>
            {/* @ts-ignore: error after upgrading to ux-configs */}
            <Saving intlNamespace="usf.price" {...saving} />
          </When>
          <When condition={Boolean(badge)}>
            <span className={classNames.badge}>
              <Badge kind={BadgeKind.PRIMARY}>{badge}</Badge>
            </span>
          </When>
          {/* @ts-ignore: error after upgrading to ux-configs */}
          <When condition={hasDiscount}>
            <span className={classNames.badge}>
              <Badge kind={BadgeKind.HIGHLIGHT}>
                <FormattedMessage
                  id="usf.price.saving.percentage"
                  values={{
                    saving: `${parseInt(String(discountPerc), 10)}%`,
                  }}
                />
              </Badge>
            </span>
          </When>
        </Choose>
        <div className={classNames.pricing}>
          {hasProposition && (
            <Heading06 className={classNames.intro} component="div">
              <Shortcode against={colorway} code={proposition} />
            </Heading06>
          )}
          <span>
            <FormattedMessage
              id="usf.price.display"
              values={{
                price: (
                  <Heading04 className={classNames.price} component="span">
                    <Prices amount={price} currency={currency} />
                  </Heading04>
                ),
                renewal: (
                  <Heading06 className={classNames.renewal} component="span">
                    <RenewalPeriod period={renewalPeriod} />
                  </Heading06>
                ),
              }}
            />
          </span>
        </div>
      </div>
      {showSetupFee && (
        <div className={classNames.setupFee}>
          <FormattedMessage
            id="usf.price.additionalFee"
            values={{
              fee: (
                <span className={classNames.fee}>
                  <Prices
                    // @ts-ignore: error after upgrading to ux-configs
                    amount={upFront}
                    currency={currency}
                  />
                </span>
              ),
            }}
          />
        </div>
      )}
      {showLegalCaveat && (
        <div className={classNames.legalCaveat}>
          <LegalText bespoke={legalText} product={product} pricingStrategy={pricingStrategy} />
        </div>
      )}
    </Colorway>
  );
};

ProductPrice.displayName = 'ProductPrice';

export default ProductPrice;
